import React, { useState, useEffect, useCallback } from 'react';
import { motion, AnimatePresence } from 'framer-motion';
import { Trophy, RotateCcw, Timer, Bomb } from 'lucide-react';
import { useGameState } from './hooks/useGameState';
import { useTimer } from './hooks/useTimer';
import { formatTime } from './utils';
import { getScores, postScore } from './api';
import { Score } from './types';
import ScoreBoardModal from './components/ScoreBoardModal';
import HelpModal from './components/HelpModal';
import GameSquare from './components/GameSquare';
import './GameBoard.css';

const GameBoard: React.FC = () => {
  const {
    squares,
    gameOver,
    winner,
    flagCount,
    mineTotal,
    boardSize,
    initializeGame,
    clickSquare,
    toggleFlag,
  } = useGameState();

  const { seconds, start, stop, reset } = useTimer();
  
  const [playerName, setPlayerName] = useState('Anonymous');
  const [scores, setScores] = useState<Score[]>([]);
  const [scoresModalOpen, setScoresModalOpen] = useState(false);
  const [helpModalOpen, setHelpModalOpen] = useState(false);
  const [isRecordTime, setIsRecordTime] = useState(false);
  const [longPressTimeout, setLongPressTimeout] = useState<NodeJS.Timeout | null>(null);

  // Load scores on component mount
  useEffect(() => {
    loadScores();
  }, []);

  // Initialize game on component mount
  useEffect(() => {
    initializeGame();
  }, [initializeGame]);

  // Handle game over state
  useEffect(() => {
    if (gameOver) {
      stop();
      if (winner) {
        checkForRecordTime();
      }
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [gameOver, winner, stop, seconds]);

  const loadScores = async () => {
    const fetchedScores = await getScores();
    setScores(fetchedScores);
  };

  const checkForRecordTime = () => {
    if (scores.length === 0 || seconds < scores[scores.length - 1].score || scores.length < 10) {
      setIsRecordTime(true);
    }
  };

  const handleSubmitScore = async () => {
    const success = await postScore(playerName, seconds);
    if (success) {
      await loadScores();
      setScoresModalOpen(true);
    }
    handleNewGame();
  };

  const handleNewGame = () => {
    reset();
    setIsRecordTime(false);
    setPlayerName('Anonymous');
    initializeGame();
  };

  const handleSquareClick = useCallback((index: number) => {
    if (squares.length === 0) return;
    
    // Start timer on first click
    if (seconds === 0 && !gameOver) {
      start();
    }
    
    const hitMine = clickSquare(index);
    if (hitMine) {
      stop();
    }
  }, [squares, seconds, gameOver, start, stop, clickSquare]);

  const handleLongPress = useCallback((index: number) => {
    const timeout = setTimeout(() => {
      toggleFlag(index);
      setLongPressTimeout(null);
    }, 500);
    setLongPressTimeout(timeout);
  }, [toggleFlag]);

  const handleLongPressEnd = useCallback(() => {
    if (longPressTimeout) {
      clearTimeout(longPressTimeout);
      setLongPressTimeout(null);
    }
  }, [longPressTimeout]);

  const handleRightClick = useCallback((index: number) => {
    if (squares.length === 0 || gameOver) return;
    toggleFlag(index);
  }, [squares, gameOver, toggleFlag]);

  const toggleModal = (modalType: 'scores' | 'help') => {
    if (modalType === 'scores') {
      setScoresModalOpen(!scoresModalOpen);
    } else {
      setHelpModalOpen(!helpModalOpen);
    }
  };

  const renderGameBoard = () => {
    if (squares.length === 0) return null;

    const rows = [];
    for (let i = 0; i < boardSize; i++) {
      const rowSquares = [];
      for (let j = 0; j < boardSize; j++) {
        const index = i * boardSize + j;
        const square = squares[index];
        
        rowSquares.push(
          <GameSquare
            key={index}
            square={square}
            gameOver={gameOver}
            onSquareClick={() => handleSquareClick(index)}
            onLongPress={() => handleLongPress(index)}
            onLongPressEnd={handleLongPressEnd}
            onRightClick={() => handleRightClick(index)}
          />
        );
      }
      rows.push(<tr key={i}>{rowSquares}</tr>);
    }
    
    return (
      <div className="game-table-container">
        <table className="game-table">
          <tbody>{rows}</tbody>
        </table>
        {renderGameOverModal()}
      </div>
    );
  };

  const renderGameOverModal = () => {
    if (!gameOver) return null;

    return (
      <AnimatePresence>
        <div className="game-over-overlay">
          <motion.div 
            className="game-over-modal-content"
            initial={{ opacity: 0, scale: 0.8 }}
            animate={{ opacity: 1, scale: 1 }}
            exit={{ opacity: 0, scale: 0.8 }}
            transition={{ duration: 0.4, ease: "easeOut" }}
          >
          {winner ? (
            <motion.div 
              className="game-over-card success"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
            >
              <motion.div 
                className="trophy-container"
                initial={{ scale: 0, rotate: -180 }}
                animate={{ scale: 1, rotate: 0 }}
                transition={{ delay: 0.4, type: "spring", stiffness: 260, damping: 20 }}
              >
                <Trophy size={48} className="trophy-icon" />
              </motion.div>
              <h2 className="victory-title">Victory!</h2>
              <p className="victory-time">Time: {formatTime(seconds)}</p>
              {isRecordTime && (
                <motion.div 
                  className="record-input-container"
                  initial={{ opacity: 0, y: 10 }}
                  animate={{ opacity: 1, y: 0 }}
                  transition={{ delay: 0.6 }}
                >
                  <p className="record-label">🏆 New Record!</p>
                  <input
                    className="name-input"
                    type="text"
                    value={playerName}
                    onChange={(e) => setPlayerName(e.target.value.slice(0, 14))}
                    onFocus={() => setPlayerName('')}
                    placeholder="Enter your name"
                    maxLength={14}
                  />
                </motion.div>
              )}
              <motion.button 
                className="new-game-button success"
                onClick={handleSubmitScore}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                New Game
              </motion.button>
            </motion.div>
          ) : (
            <motion.div 
              className="game-over-card failure"
              initial={{ y: 20, opacity: 0 }}
              animate={{ y: 0, opacity: 1 }}
              transition={{ delay: 0.2, duration: 0.4 }}
            >
              <motion.div 
                className="bomb-container"
                initial={{ scale: 0 }}
                animate={{ scale: [0, 1.2, 1] }}
                transition={{ delay: 0.4, duration: 0.6 }}
              >
                <Bomb size={48} className="bomb-icon" />
              </motion.div>
              <h2 className="failure-title">Game Over</h2>
              <p className="failure-message">Better luck next time!</p>
              <motion.button 
                className="new-game-button failure"
                onClick={handleNewGame}
                whileHover={{ scale: 1.05 }}
                whileTap={{ scale: 0.95 }}
              >
                Try Again
              </motion.button>
            </motion.div>
          )}
          </motion.div>
        </div>
      </AnimatePresence>
    );
  };

  return (
    <motion.div 
      className="game-board" 
      role="application" 
      aria-label="Game Board"
      initial={{ opacity: 0, y: 20 }}
      animate={{ opacity: 1, y: 0 }}
      transition={{ duration: 0.5 }}
    >
      <motion.div 
        className="game-header"
        initial={{ opacity: 0, scale: 0.9 }}
        animate={{ opacity: 1, scale: 1 }}
        transition={{ delay: 0.2, duration: 0.4 }}
      >
        <div className="timer-container">
          <Timer size={18} className="timer-icon" />
          <div className="timer">{formatTime(seconds)}</div>
        </div>
        <motion.button 
          className="reset-button"
          onClick={handleNewGame}
          title="Reset Game"
          whileHover={{ scale: 1.1, rotate: 180 }}
          whileTap={{ scale: 0.95 }}
          transition={{ type: "spring", stiffness: 400, damping: 25 }}
        >
          <RotateCcw size={20} />
        </motion.button>
        <div className="mine-counter">
          <Bomb size={16} className="mine-icon" />
          <span>{mineTotal - flagCount}</span>
        </div>
      </motion.div>
      
      <div className="game-area">
        {renderGameBoard()}
      </div>
      
      <div className="game-footer">
        <button 
          className="button is-link is-small"
          onClick={() => toggleModal('scores')}
        >
          Scores
        </button>
        <span className="mx-2">|</span>
        <button 
          className="button is-link is-small"
          onClick={() => toggleModal('help')}
        >
          Help
        </button>
        <span className="mx-2">|</span>
        <a
          href="https://github.com/bshandley"
          target="_blank"
          rel="noopener noreferrer"
          className="button is-link is-small"
        >
          GitHub
        </a>
      </div>

      <ScoreBoardModal
        isOpen={scoresModalOpen}
        scores={scores}
        onClose={() => toggleModal('scores')}
      />
      
      <HelpModal
        isOpen={helpModalOpen}
        onClose={() => toggleModal('help')}
      />
    </motion.div>
  );
};

export default GameBoard;
